import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarClock } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";

const DAYS = [
  { key: "sunday", label: "Sunday" }, { key: "monday", label: "Monday" }, { key: "tuesday", label: "Tuesday" },
  { key: "wednesday", label: "Wednesday" }, { key: "thursday", label: "Thursday" }, { key: "friday", label: "Friday" },
  { key: "saturday", label: "Saturday" },
];
const EMPTY = { sunday: 1, monday: 1, tuesday: 1, wednesday: 1, thursday: 1, friday: 1, saturday: 1 };

export function DeliveryDaysCard() {
  const [days, setDays] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiClient.get("/outlet/deliver-days")
      .then(({ data }) => setDays(data))
      .catch((error) => toast.error(getApiErrorMessage(error, "Could not load the delivery schedule.")))
      .finally(() => setLoading(false));
  }, []);

  const toggleDay = (key) => setDays((d) => ({ ...d, [key]: d[key] ? 0 : 1 }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.put("/outlet/deliver-days", days);
      setDays(data);
      toast.success("Weekly delivery schedule updated.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not update the delivery schedule."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="settings-delivery-days-card">
      <CardContent className="p-5">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <CalendarClock size={18} strokeWidth={1.5} />
          </span>
          <div className="min-w-0 flex-1">
            <p className="font-heading text-sm font-semibold text-text-main">Weekly Delivery Schedule</p>
            <p className="mt-0.5 text-xs text-text-muted">
              Pick the days your business actually delivers on. Days you turn off here are blocked everywhere —
              no deliveries get generated, and customers/subscriptions can't select that day either.
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-4">
          {DAYS.map((d) => (
            <label key={d.key} className="flex items-center gap-2 text-sm text-text-main" data-testid={`settings-delivery-day-${d.key}`}>
              <Checkbox checked={Boolean(days[d.key])} disabled={loading} onCheckedChange={() => toggleDay(d.key)} />
              {d.label}
            </label>
          ))}
        </div>

        <div className="mt-4 flex justify-end">
          <Button size="sm" onClick={handleSave} disabled={saving || loading} data-testid="settings-delivery-days-save-button">
            {saving ? "Saving..." : "Save Schedule"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
